<motn-drawer-gap-from-top
  [addToZIndex]="1"
  (clickedOutside$)="close()"
  [loading]="!tile || formulaDeviceLoading()"
>
  @if (tile) {
    <h3>
      Configure Tile

      <motn-pill
        [squareLetter]="'t'"
      >{{ tileId }}</motn-pill>

      @if (deviceId && !tile.isFormulaTile()) {
        <motn-pill
          [squareLetter]="'d'"
          class="clickable"
          (click)="navigateToDevice()"
        >{{ deviceId }}</motn-pill>
      }

      @if (shouldShowMetadata) {
        <motn-pill
          [color]="'blue'"
          class="point-id clickable"
          (click)="showMetadata()"
        >View Metadata</motn-pill>
      } @else if (displayPointId) {
        <motn-pill
          [color]="'blue'"
          [monospace]="true"
          class="point-id"
        >{{ displayPointId }}</motn-pill>
      }
    </h3>

    <div
      class="content"
    >
      @if (tile.typeSupportsPoints()) {
        <div class="content">
          <h2>Data Source</h2>

          @if (tile.isFormulaTile()) {
            @if (formulaDeviceLoading()) {
              <p class="text-secondary">Loading formula details…</p>
            } @else if (!formulaDevice() && !_tileSimpleStructure?.DeviceID) {
              <p class="text-secondary">Formula device has not been configured yet.</p>
            } @else if (!formulaDevice()) {
              <p class="text-secondary">Formula device could not be loaded.</p>
            } @else {
              <div class="field">
                @if (formulaIsCustom) {
                  <div class="ds-formula-display">
                    @for (seg of formulaExpressionSegments; track $index) {
                      @if (seg.isVariable) {
                        <motn-formula-variable-pill
                          [name]="seg.value"
                          [color]="'green'"
                          [readonly]="true"
                        ></motn-formula-variable-pill>
                      } @else {
                        <span>{{ seg.value }}</span>
                      }
                    }
                  </div>
                } @else {
                  <span class="ds-value">{{ formulaDisplayName }}</span>
                }
                <div class="label">Formula</div>
              </div>

              <div class="ds-variables">
                @for (v of formulaVariableInfos; track v.key) {
                  <div class="ds-variable-card">
                    <motn-formula-variable-pill
                      [name]="v.label"
                      [color]="pillColorForVar(v)"
                      [readonly]="true"
                    ></motn-formula-variable-pill>

                    <div class="field">
                      <div class="ds-name-row">
                        <span class="ds-value">{{ v.deviceName }}</span>
                        @if (v.deviceId) {
                          <motn-pill
                            [squareLetter]="'d'"
                            class="clickable"
                            (click)="navigateToDevice(v.deviceId)"
                          >{{ v.deviceId }}</motn-pill>
                        }
                      </div>
                      <div class="label">Device</div>
                    </div>

                    <div class="field">
                      <div class="ds-point-row">
                        <span class="ds-value">{{ v.pointDisplay }}</span>
                        @if (v.pointValue != null) {
                          <motn-canvas-mini-tile
                            [directValue]="v.pointValue.value"
                            [directValueTime]="v.pointValue.time ?? ''"
                            [directValueOffset]="v.transformations?.Offset ?? 0"
                            [hideIcon]="true"
                            [compact]="true"
                          ></motn-canvas-mini-tile>
                        }
                      </div>
                      <div class="label">Device Point</div>
                    </div>

                    @if (v.transformations) {
                      <div class="ds-transformations">
                        @if (v.transformations.Multiplier != null) {
                          <div class="field">
                            <span class="ds-value">{{ multiplierLabel(v.transformations.Multiplier) }}</span>
                            <div class="label">Multiplier</div>
                          </div>
                        }
                        <div class="field">
                          <span class="ds-value">{{ conversionLabel(v.transformations.Conversion) }}</span>
                          <div class="label">Conversion</div>
                        </div>
                        @if (v.transformations.Offset != null) {
                          <div class="field">
                            <span class="ds-value">{{ v.transformations.Offset }}</span>
                            <div class="label">Calibration Offset</div>
                          </div>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            }
          } @else {
            <div class="ds-row">
              <div class="field">
                <div class="ds-name-row">
                  <span class="ds-value">{{ deviceDisplayName }}</span>
                  @if (deviceId) {
                    <motn-pill
                      [squareLetter]="'d'"
                      class="clickable"
                      (click)="navigateToDevice()"
                    >{{ deviceId }}</motn-pill>
                  }
                </div>
                <div class="label">Device</div>
              </div>
              <div class="field">
                <span class="ds-value">{{ pointDisplayName }}</span>
                <div class="label">Device Point</div>
              </div>
            </div>
          }
        </div>
      }

      <div
        class="content"
      >
        <h2>
          Basics
        </h2>

        <div class="flex">
          <div>
            <input
              [(ngModel)]="tile.Name"
              (ngModelChange)="updateSaveStatus()"
            >
            <div
              class="label"
            >
              Tile Name
            </div>
          </div>

          <div>
            <input
              [(ngModel)]="tile.Appearance.DisplayName"
              (ngModelChange)="updateSaveStatus()"
            >
            <div
              class="label"
            >
              Display Name
            </div>
          </div>

          @if (tile.typeSupportsReadWrite() && _tileSimpleStructure.Protocol !== 'lorawan') {
            <div>
              <select
                [(ngModel)]="_tileSimpleStructure.Writable"
                (ngModelChange)="updateSaveStatus()"
              >
                <option
                  [ngValue]="false"
                  [innerText]="'Read-Only'"
                ></option>
                <option
                  [ngValue]="true"
                  [innerText]="'Read+Write'"
                ></option>
              </select>
              <div
                class="label"
              >
                Writability
              </div>
            </div>
          }

          @if (_tileSimpleStructure.Protocol === 'bacnet' && _tileSimpleStructure.Writable) {
            <div>
              <select
                [(ngModel)]="_tileSimpleStructure.Priority"
                (ngModelChange)="updateSaveStatus()"
              >
                @for (option of bacnetPriorityOptions; track option) {
                  <option
                    [ngValue]="option"
                    [innerText]="option"
                  ></option>
                }
              </select>
              <div
                class="label"
              >
                BACnet Priority Level

                <button
                  (click)="fetchBacnetPriorityArray()"
                  [ngClass]="{
                    'small': 1,
                    'white': 1,
                    'loading': fetchingBacnetPriorityArray
                  }"
                >
                  <span>Get Current Priority Array</span>
                  <motn-spinner></motn-spinner>
                </button>
              </div>
            </div>
          }

          @if (tile.Type === 'SIMPLE_ANALOG' && _tileSimpleStructure.Writable) {
            <div>
              <select
                [(ngModel)]="_tileAppearance.Config.Increment"
                (ngModelChange)="updateSaveStatus()"
              >
                @for (option of incrementOptions; track option) {
                  <option
                    [ngValue]="option"
                    [innerText]="option"
                  ></option>
                }
              </select>
              <div
                class="label"
              >
                Increment +/-
              </div>
            </div>
          }

          @if (tile.Type === 'SIMPLE_ANALOG' && _tileSimpleStructure.Writable) {
            <div>
              <input
                [(ngModel)]="tile.Appearance.Config.MinAllowed"
                [ngClass]="{'error': minMaxError}"
                (ngModelChange)="updateSaveStatus(); validateMinMax()"
                (blur)="validateMinMax()"
                type="tel"
              >
              <div
                class="label"
              >
                Minimum Writable Value
              </div>
            </div>
          }

          @if (tile.Type === 'SIMPLE_ANALOG' && _tileSimpleStructure.Writable) {
            <div>
              <input
                [(ngModel)]="tile.Appearance.Config.MaxAllowed"
                [ngClass]="{'error': minMaxError}"
                (ngModelChange)="updateSaveStatus(); validateMinMax()"
                (blur)="validateMinMax()"
                type="tel"
              >
              <div
                class="label"
              >
                Maximum Writable Value
              </div>
            </div>
          }
        </div>

      </div>

      <div class="content">
        <h2>Tags</h2>

        <div class="tags-row">
          <div class="profile-field">
            <div class="facet-select-wrap" cdkOverlayOrigin #profileOrigin="cdkOverlayOrigin">
              <button class="select-trigger" #profileTrigger (click)="toggleProfileMenu()">
                <span>{{ selectedProfileLabel }}</span>
                <i class="fa-regular fa-chevron-down"></i>
              </button>
              @if (profileMenuOpen) {
                <motn-popover-multi-select
                  [externalOrigin]="profileOrigin"
                  [triggerEl]="profileTrigger"
                  [optionGroup]="profileOptions"
                  [multiOrSingle]="'single'"
                  [autoFocusSearch]="false"
                  [overlayPositions]="facetOverlayPositions"
                  (clickedOutsideMenu$)="closeProfileMenu()"
                  (clickedOption$)="onProfileSelected($event)"
                ></motn-popover-multi-select>
              }
            </div>
            <span class="label">Profile</span>
          </div>

          <div class="tag-input-field-wrap">
            <app-tag-input
              [objectType]="ObjectType.TILE"
              [existingTags]="pendingTileTags"
              (tagsChanged)="onTagsChanged($event)"
            ></app-tag-input>
            <span class="label">Tags</span>
          </div>
        </div>

        @if (selectedProfile) {
          <div class="facet-fields">
            @for (facet of profileFacets; track facet.ID) {
              <div class="facet-field">
                <div class="facet-select-wrap" cdkOverlayOrigin #facetOrigin="cdkOverlayOrigin">
                  <div
                    class="select-trigger"
                    #facetTrigger
                    role="button"
                    tabindex="0"
                    (click)="toggleFacet(facet.ID)"
                    (keydown.enter)="toggleFacet(facet.ID)"
                    (keydown.space)="$event.preventDefault(); toggleFacet(facet.ID)"
                  >
                    <span [class.placeholder]="!pendingFacetValues[facet.ID]">
                      {{ pendingFacetValues[facet.ID]?.Value }}
                    </span>
                    <span class="select-trigger-icons">
                      @if (pendingFacetValues[facet.ID]) {
                        <button type="button" class="clear-btn" (click)="clearFacet(facet.ID, $event)">
                          <i class="fa-regular fa-circle-xmark"></i>
                        </button>
                      }
                      <i class="fa-regular fa-chevron-down"></i>
                    </span>
                  </div>
                  @if (openFacetKey === facet.ID) {
                    <motn-popover-multi-select
                      [externalOrigin]="facetOrigin"
                      [triggerEl]="facetTrigger"
                      [optionGroup]="getFacetOptionGroup(getOptionsForFacet(facet.ID), facet.ID)"
                      [multiOrSingle]="'single'"
                      [autoFocusSearch]="true"
                      [capitalizeInput]="true"
                      [createLabel]="'Create'"
                      [createRequiresInput]="true"
                      [overlayPositions]="facetOverlayPositions"
                      (clickedOutsideMenu$)="openFacetKey = null"
                      (clickedOption$)="onFacetOptionClicked(facet.ID, $event)"
                      (createClicked$)="onFacetCreate(facet.ID, $event)"
                    ></motn-popover-multi-select>
                  }
                </div>
                <span class="label">{{ facet.Label }}</span>
              </div>
            }
          </div>
        }
      </div>

      @if (tile.Type === 'SUPER_FOLDER') {
        <div
          class="content"
        >
          <div class="content-header">
            <h2>
              Folder Contents
            </h2>
            <div class="view-mode">
              View mode:
              <motn-folder-view-flip-toggle
                [displayMode]="folderDisplayMode"
                (displayModeChange)="updateDisplayMode($event)"
              ></motn-folder-view-flip-toggle>
            </div>
          </div>
          @if (isDataPointsViewMode && folderContentsData()) {
            <div class="scrollable-tiles">
              Height of the scrollable area:
              <div class="scrollable-height-input">
                <input
                  (click)="showScrollableHeightMenu()"
                  [value]="heightQuantity"
                  readonly
                  type="number"
                >
                @if (scrollableHeightMenu) {
                  <motn-popover-menu
                    (clickedOption$)="scrollableHeightMenuOptionSelected($event)"
                    (clickedOutsideMenu$)="scrollableHeightMenu = false"
                    [optionGroup]="scrollableHeightMenuOptions"
                    [overlayPositions]="scrollableHeightPopoverPositions"
                  ></motn-popover-menu>
                }
              </div>
            </div>
          }
          <motn-tile-folder-contents-editor
            [tile]=tile
            [(folderContentsData)]="folderContentsData"
            [cleanupDeletedTilesTrigger]="cleanupDeletedTilesTrigger()"
            (updatedOrder$)="updateSaveStatus()"
            (updatedHiddenDataPoints$)="updateSaveStatus()"
          ></motn-tile-folder-contents-editor>
        </div>
      }

      @if (tile.Type === 'SUPER_CANVAS') {
        <motn-canvas-tile-editor
          [tileId]="tileId"
          [canvasStructure]="_tileCanvasStructure"
          (structureChange)="onCanvasStructureChange($event)"
        ></motn-canvas-tile-editor>
      }

      @if (!tile.typeSupportsHavingDescendants()) {
        <div
          class="content"
        >
          <h2>
            Color
          </h2>

          <motn-color-picker
            [color]="tile.Appearance.Color"
            (colorPicked$)="updateColor($event)"
          ></motn-color-picker>
        </div>
      }

      @if (tile.typeSupportsPoints()) {
        <div
          class="content"
        >
          <h2>
            Data Display

            @if (tile.Type === 'SIMPLE_MULTISTATE') {
              <button
                class="green small"
                (click)="addNewState()"
              >
                <i class="far fa-plus"></i> Add State
              </button>
            }
          </h2>

          @if (tile.Type === 'SIMPLE_BOOL') {
            <div
              class="flex"
            >
              <div>
                <input
                  [(ngModel)]="_tileSimpleBoolStructure.DisplayTrue"
                  (ngModelChange)="prepareConditions(); updateSaveStatus()"
                >
                <div
                  class="label"
                >
                  Phrase shown when value is true, 1, on, etc.
                </div>
              </div>

              <div>
                <input
                  [(ngModel)]="_tileSimpleBoolStructure.DisplayFalse"
                  (ngModelChange)="prepareConditions(); updateSaveStatus()"
                >
                <div
                  class="label"
                >
                  Phrase shown when value is false, 0, off, etc.
                </div>
              </div>
            </div>
          }

          @if (tile.Type === 'SIMPLE_ANALOG') {
            <div
              class="flex"
            >
              <div>
                <input
                  [(ngModel)]="_tileSimpleAnalogStructure.DisplayRawUnits"
                  (ngModelChange)="updateSaveStatus()"
                >
                <div
                  class="label"
                >
                  Data Units (e.g. °F, %, psi, lbf/in², etc.)
                </div>
              </div>

              <div>
                <select
                  [(ngModel)]="_tileAppearance.Config.Decimal"
                  (ngModelChange)="updateSaveStatus()"
                >
                  @for (option of decimalOptions; track option) {
                    <option
                      [ngValue]="option"
                      [innerText]="option"
                    ></option>
                  }
                </select>
                <div
                  class="label"
                >
                  Decimal Places
                </div>
              </div>
            </div>
          }

          @if (tile.Type === 'SIMPLE_MULTISTATE') {
            <div>
              <motn-sort-table
                [table$]="stateTable$"
              ></motn-sort-table>

              @if (_tileSimpleStructure.Protocol === 'bacnet') {
                <button
                  class="small"
                  style="margin-top: 10px"
                  (click)="showOriginalStates()"
                >
                  Show Original States
                </button>
              }
            </div>
          }
        </div>
      }

      @if (tile.Type === 'SIMPLE_ANALOG') {
        <div
          class="content"
        >
          <h2>
            Data Transformation
          </h2>

          <div class="flex">
            <div>
              <select
                [(ngModel)]="_tileAppearance.Config.Conversion"
                (ngModelChange)="updateSaveStatus()"
              >
                @for (option of conversionOptions; track option) {
                  <option
                    [ngValue]="option.id"
                    [innerText]="option.name"
                  ></option>
                }
              </select>
              <div
                class="label"
              >
                Conversion Formula
              </div>
            </div>

            <div>
              <select
                [(ngModel)]="_tileAppearance.Config.Multiplier"
                (ngModelChange)="updateSaveStatus()"
              >
                @for (option of multiplierOptions; track option) {
                  <option
                    [ngValue]="+option.id"
                    [innerText]="option.name"
                  ></option>
                }
              </select>
              <div
                class="label"
              >
                Multiplier
              </div>
            </div>

            <div>
              <input
                [(ngModel)]="_tileAppearance.Config.Offset"
                [ngModelOptions]="{updateOn: 'blur'}"
                (ngModelChange)="updateSaveStatus()"
                (blur)="validateOffset()"
                type="number"
                step="any"
              >
              <div
                class="label"
              >
                Calibration Offset
              </div>
            </div>
          </div>
        </div>
      }

      @if (tile.typeSupportsAlarms() && conditions) {
        <div
          class="content"
        >
          <h2>
            Alarms
          </h2>

          <div class="flex">
            <div>
              <select
                [(ngModel)]="_tileConditionsStructure.AutoClear"
                (ngModelChange)="updateSaveStatus()"
              >
                @for (option of autoClearOptions; track option) {
                  <option
                    [ngValue]="option.id"
                    [innerText]="option.name"
                  ></option>
                }
              </select>
              <div
                class="label"
              >
                Auto-Clear Alarms
              </div>
            </div>
          </div>

          <div class="flex">
            @for (alarmConfig of alarmConfigs; track alarmConfig; let i = $index) {
              <div
                class="box"
              >
                <div class="title">
                  <h3>
                    @if (alarmConfig.Title) {
                      <span
                        [ngClass]="alarmConfig.Color"
                      >
                        <i [ngClass]="['fa-solid', alarmConfig.Icon]"></i>
                      </span>
                    }

                    {{alarmConfig.Title}}
                  </h3>

                  <label
                    class="toggle"
                  >
                    <input
                      [(ngModel)]="conditions[i].Enabled"
                      (ngModelChange)="validateConditions(); updateSaveStatus()"
                      type="checkbox"
                    >
                    <span class="slider"></span>
                  </label>
                </div>

                @if (conditions[i].Enabled) {
                  <div
                    class="condition-config"
                  >
                    @if (tile.Type === 'SIMPLE_ANALOG') {
                      <div
                        class="flex"
                      >
                        <div class="label">
                          When Value Is...
                        </div>

                        <div class="inputs">
                          <div class="left">
                            @if (alarmConfig.Direction === 'high') {
                              <select
                                [(ngModel)]="conditions[i].Comparison.Operator"
                                (ngModelChange)="updateSaveStatus()"
                              >
                                @for (operator of highAlarmOperators; track operator) {
                                  <option
                                    [ngValue]="operator"
                                    [innerText]="operator"
                                  ></option>
                                }
                              </select>
                            }

                            @if (alarmConfig.Direction === 'low') {
                              <select
                                [(ngModel)]="conditions[i].Comparison.Operator"
                                (ngModelChange)="updateSaveStatus()"
                              >
                                @for (operator of lowAlarmOperators; track operator) {
                                  <option
                                    [ngValue]="operator"
                                    [innerText]="operator"
                                  ></option>
                                }
                              </select>
                            }
                          </div>

                          <div class="right">
                            <input
                              [(ngModel)]="conditions[i].Comparison.Value"
                              [ngModelOptions]="{updateOn: 'blur'}"
                              [ngClass]="{'error': conditionErrors[i]}"
                              (ngModelChange)="validateConditions(); updateSaveStatus()"
                              (input)="validateConditionsLive(i, $any($event.target).value)"
                              [type]="'number'"
                            >
                          </div>
                        </div>
                      </div>
                    }

                    @if (tile.Type !== 'SIMPLE_ANALOG') {
                      <div
                        class="flex"
                      >
                        <div class="label">
                          When Value Is...
                        </div>

                        <div class="inputs">
                          <select
                            [(ngModel)]="conditions[i].Comparison.Value"
                            (ngModelChange)="updateSaveStatus()"
                          >
                            @for (option of nonAnalogAlarmValueOptions; track option) {
                              <option
                                [ngValue]="+option.id"
                                [innerText]="option.name"
                              ></option>
                            }
                          </select>
                        </div>
                      </div>
                    }

                    <div class="flex">
                      <div class="label">
                        Delay Alarm For:
                      </div>

                      <div class="inputs">
                        <div>
                          <input
                            [(ngModel)]="conditions[i].Delay"
                            (mouseup)="validateDelays()"
                            (keyup)="validateDelays()"
                            (ngModelChange)="updateSaveStatus()"
                            [type]="'number'"
                          >
                        </div>

                        <div>
                          seconds
                        </div>
                      </div>
                    </div>

                    <div class="flex">
                      <div class="label">
                        Show Message:
                      </div>

                      <div class="inputs">
                        <textarea
                          [(ngModel)]="conditions[i].DisplayText"
                          (ngModelChange)="updateSaveStatus()"
                        ></textarea>
                      </div>
                    </div>

                    <div class="flex">
                      <div class="label">
                        Show Icon:
                      </div>

                      <div class="inputs">
                        <button
                          (click)="pickingIconCondition = i"
                          [ngClass]="alarmConfig.Color"
                          [innerHtml]="'<i class=\'fa-regular fa-'+conditions[i].Icon+'\'>'"
                        ></button>

                        @if (pickingIconCondition === i) {
                          <motn-popover-icon-picker
                            [icon]="conditions[i].Icon"
                            (clickedIcon$)="conditions[i].Icon = $event; pickingIconCondition = null; updateSaveStatus()"
                            (clickedOutsideMenu$)="pickingIconCondition = null"
                          ></motn-popover-icon-picker>
                        }
                      </div>
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }

      <div
        class="content"
      >
        <h2>
          Appearance
        </h2>

        <motn-icon-picker
          [icon]="tile.Appearance.Icon"
          (iconPicked$)="updateIcon($event)"
        ></motn-icon-picker>
      </div>

      @if (tile.Type === 'SUPER_CANVAS') {
        <div
          class="content"
        >
          <h2>
            Color
          </h2>

          <motn-color-picker
            [color]="tile.Appearance.Color"
            (colorPicked$)="updateColor($event)"
          ></motn-color-picker>
        </div>
      }

      <div
        class="content"
      >
        <h2>
          Apps
        </h2>

        <div class="apps-section">
          <motn-pill-multiselect
            [items]="appPillOptions"
            [selected]="appPillSelected"
            [openMenu$]="appPillOpenMenu$"
            (add)="onAppPillAdd($event)"
            (remove)="onAppPillRemove($event)"
          ></motn-pill-multiselect>

          <button
            class="small"
            (click)="triggerAppPillMenu()"
          >
            Link/Unlink Apps
          </button>
        </div>
      </div>

      <motn-save-revert-buttons
        #saveRevertButtons
        [small]="false"
        (clickedSave$)="save()"
        (clickedRevert$)="revert()"
      ></motn-save-revert-buttons>

      <button
        (click)="delete()"
        class="red"
      >
        Delete
      </button>

      <button
        (click)="close()"
        class="alt"
      >
        Close
      </button>
    </div>
  }
</motn-drawer-gap-from-top>

@if (bacnetPriorityArray) {
  <motn-drawer-gap-from-top
    [addToTopGap]="60"
    (clickedOutside$)="bacnetPriorityArray = null"
  >
    <h3>
      BACnet Priority Array
      <motn-pill
        [squareLetter]="'t'"
      >{{ tileId }}</motn-pill>
    </h3>

    <div class="priority-array">
      <div>
        <motn-sort-table
          [table$]="bacnetPriorityArrayTable$"
        ></motn-sort-table>
      </div>

      <button
        (click)="bacnetPriorityArray = null"
      >
        Close
      </button>
    </div>
  </motn-drawer-gap-from-top>
}
